import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  or,
  setDoc,
  Unsubscribe,
  updateDoc,
  where, Query,
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable } from 'rxjs';
import { CommerceVariation } from '../models/commerce-variation.model';
import { CommerceCategory } from '../models/commerce-category.model';
import { filter } from 'rxjs/operators';
import { CommercePriceGrid } from '../models/commerce-price-grid.model';
import {
  OptionConverters,
  OptionObject,
  OptionTypeToVariationFields
} from '../models/commerce-configuration-option.model';
import { ObjectHelper } from '../helpers/object.helper';
import { WebProduct } from '../models/web-product.model';
import { Format } from '../models/format.model';
import { UploadService } from './upload.service';
import { Packaging } from "../models/packaging.model";
import { read, utils, WorkBook, writeFileXLSX } from "xlsx";
import { UnitTypeEnum } from "../enums/unit-type.enum";
import { TranslateService } from "@ngx-translate/core";
import * as Papa from 'papaparse';
import { ProductService } from './product.service';
import { GridUpdate } from "../models/grid-update.model";

@Injectable({
  providedIn: 'root',
})
export class CommerceService {
  private firestore = inject(Firestore);
  private storageService = inject(UploadService);
  private translate = inject(TranslateService);
  private productService = inject(ProductService);

  private categories$: Record<string, BehaviorSubject<CommerceCategory[]>> = {};
  private unsubscribeCategories: Unsubscribe;
  private options$: [string, BehaviorSubject<{ [type in keyof OptionObject]: OptionObject[type][] }>];
  private optionsInitialized: { [type: string]: boolean };
  private unsubscribeOptions: Unsubscribe[] = [];

  /**
   * Update the configuration option of a given type
   * @param type The option type
   * @param option The option object
   * @returns Firebase object id
   */
  updateConfigurationOption<T extends keyof OptionObject>(type: T, option: OptionObject[T]): Promise<string> {
    const col = collection(this.firestore, type);
    const ref = (option.id ? doc(col, option.id) : doc(col))
      .withConverter(OptionConverters[type]);
    return setDoc(ref, option, { merge: true })
      .then(() => ref.id);
  }

  /**
   * Delete the configuration option of a given type
   * @param type The option type
   * @param option The option object
   * @returns Firebase object id
   */
  async configurationsByOption<T extends keyof OptionObject>(type: T, option: OptionObject[T]):
    Promise<CommerceVariation[]> {
    const q = query(
      collection(this.firestore, 'variation'),
      or(...OptionTypeToVariationFields[type].map(field => where(field, '==', option.id)))
    ).withConverter(CommerceVariation.converter);

    const snap = await getDocs(q);
    if (snap.empty) {
      return [];
    }
    return snap.docs.map(doc => doc.data())
  }

  /**
   * Delete the configuration option of a given type
   * @param type The option type
   * @param option The option object
   * @returns Firebase object id
   */
  async deleteConfigurationOption<T extends keyof OptionObject>(type: T, option: OptionObject[T]): Promise<void> {
    if (option instanceof Format && option.gabarit.path) {
      await this.storageService.removeFile(option.gabarit.path)
    }
    const col = collection(this.firestore, type);
    const ref = (option.id ? doc(col, option.id) : doc(col))
      .withConverter(OptionConverters[type]);
    await deleteDoc(ref);
  }

  /**
   * Get all configuration options of a given type
   * @param organizationId organizationId
   * @returns An observable of configuration options
   */
  configurationOptions(organizationId: string): Observable<{ [type in keyof OptionObject]: OptionObject[type][] }> {
    const options = Object.keys(OptionConverters)
    if (this.options$?.[0] !== organizationId) {
      this.unsubscribeOptions.forEach(unsub => unsub());
      this.optionsInitialized = options.reduce((obj, o) => ({ ...obj, [o]: false }), {})
      this.options$ = [organizationId, new BehaviorSubject<{ [type in keyof OptionObject]: OptionObject[type][] }>({
        formats: [],
        materials: [],
        printTypes: [],
        finishes: [],
        cuttings: [],
        drillings: [],
        bindings: [],
        foldings: []
      })];
      this.unsubscribeOptions = options
        .map(type =>
          onSnapshot(
            query(
              collection(this.firestore, type),
              where('organizationId', '==', organizationId)
            ).withConverter(OptionConverters[type]),
            snapshot => {
              this.optionsInitialized[type] = true;
              if (snapshot.empty) {
                this.options$[1].next({ ...this.options$[1].value, [type]: [] })
              }
              this.options$[1].next({ ...this.options$[1].value, [type]: snapshot.docs.map(doc => doc.data()) })
            }
          )
        );
    }
    return this.options$[1]
      .pipe(filter(() => Object.values(this.optionsInitialized).every(o => o)));
  }

  /**
   * Get variations for the given configuration, calling the onNext callback on PubSub event
   * @param organizationId organizationId
   * @param onNext The result callback
   * @returns Subscribe event
   */
  variations(organizationId: string, onNext: (variations: CommerceVariation[]) => void)
    : Unsubscribe {
    const q = query(
      collection(this.firestore, 'variation'),
      where('organizationId', '==', organizationId)
    )
      .withConverter(CommerceVariation.converter);
    return onSnapshot(q, snapshot => {
      if (snapshot.empty) {
        return onNext([]);
      }
      return onNext(snapshot.docs.map(doc => doc.data()));
    });
  }

  /**
   * Retrieves a synchronized list of CommerceVariation objects associated with the specified organization ID.
   * @param organizationId - The ID of the organization whose CommerceVariation objects are to be retrieved.
   * @returns A promise that resolves to an array of CommerceVariation objects. If no objects are found, an empty
   * array is returned.
   */
  async listSync(organizationId: string): Promise<CommerceVariation[]> {
    const q = query(
      collection(this.firestore, 'variation'),
      where('organizationId', '==', organizationId)
    ).withConverter(CommerceVariation.converter);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Get the variation of the given id
   * @param id The variation id
   * @returns The variation
   */
  variation(id: string): Promise<CommerceVariation> {
    const ref = doc(this.firestore, 'variation', id)
      .withConverter(CommerceVariation.converter);
    return getDoc(ref).then(doc => doc.data());
  }

  /**
   * Update the variation
   * @param variation The variation to update
   * @returns void
   */
  updateVariation(variation: Partial<CommerceVariation>): Promise<void> {
    const ref = doc(this.firestore, 'variation', variation.id)
      .withConverter(CommerceVariation.converter);
    return updateDoc(ref, ObjectHelper.anonymize(variation));
  }

  /**
   * Create new variation
   * @param variation The variation to update
   * @returns The created variation id
   */
  addVariation(variation: Partial<CommerceVariation>): Promise<string> {
    const ref = collection(this.firestore, 'variation')
      .withConverter(CommerceVariation.converter);
    return addDoc(ref, ObjectHelper.anonymize(variation)).then(ref => ref.id);
  }

  /**
   * Delete a variation and its price grids
   * @param variationId The variation to delete;
   */
  async delete(variationId: string): Promise<void> {
    const ref = doc(collection(this.firestore, 'variation'), variationId);
    await deleteDoc(ref);
    const snap = await getDocs(query(
      collection(this.firestore, 'price-grid'),
      where('variationId', '==', variationId)
    ));
    if (!snap.empty) {
      for (const doc of snap.docs) {
        await deleteDoc(doc.ref);
      }
    }
  }

  /**
   * Get all the products
   * @param shop The shop
   * @returns The observable
   */
  categories(shop: string): Observable<CommerceCategory[]> {
    if (!this.categories$[shop]) {
      this.categories$[shop] = new BehaviorSubject<CommerceCategory[]>([]);
      const q = query(
        collection(this.firestore, 'category'),
        where('shop', '==', shop)
      )
        .withConverter(CommerceCategory.converter);
      this.unsubscribeCategories = onSnapshot(q, snapshot => {
        if (snapshot.empty) {
          return this.categories$[shop].next([]);
        }
        this.categories$[shop].next(snapshot.docs.map(doc => doc.data()));
      });
    }
    return this.categories$[shop];
  }

  /**
   * Create a new category
   * @param category The category to create
   */
  async addCategory(category: CommerceCategory): Promise<void> {
    const ref = collection(this.firestore, 'category')
      .withConverter(CommerceCategory.converter);
    await addDoc(ref, category);
  }

  /**
   * Update the given category
   * @param category The category
   * @returns Update doc promise
   */
  updateCategory(category: Partial<CommerceCategory>): Promise<void> {
    const ref = doc(this.firestore, 'category', category.id)
      .withConverter(CommerceCategory.converter);
    return updateDoc(ref, {
      name: category.name,
      description: category.description,
      icon: category.icon,
      image: {
        name: category.image.name,
        description: category.image.description,
        path: category.image.path,
        thumb: category.image.thumb,
      }
    });
  }

  /**
   * Delete the category with the given id
   * @param id Id of the category to delete
   */
  async deleteCategory(id: string): Promise<void> {
    const ref = doc(this.firestore, 'category', id)
      .withConverter(CommerceCategory.converter);
    await deleteDoc(ref);
  }

  /**
   * Get the price grids for these variations, on PubSub event the callback is called
   * @param variation The variation id
   * @returns The Unsubscribe function
   */
  priceGrids(variation: string): Query<CommercePriceGrid> {
    return query(
      collection(this.firestore, 'price-grid'),
      where('variationId', '==', variation)
    ).withConverter(CommercePriceGrid.converter);
  }

  /**
   * Get the price grids for these variations, on PubSub event the callback is called
   * @param variation The variation id
   * @returns The Unsubscribe function
   */
  async priceGridsSync(variation: string): Promise<CommercePriceGrid[]> {
    const q = this.priceGrids(variation);
    const snap = await getDocs(q);
    if (snap.empty) {
      return [];
    }
    return snap.docs.map(doc => doc.data())
  }

  /**
   * Fetches a price grid document for a subcontractor by their unique identifier.
   * @param id - The unique identifier of the subcontractor.
   * @returns A promise that resolves to the price grid data if the document exists,
   * or null if the document does not exist.
   */
  async priceGrid(id: string): Promise<CommercePriceGrid> {
    const ref = doc(this.firestore, 'price-grid', id)
      .withConverter(CommercePriceGrid.converter);
    const document = await getDoc(ref);
    return document.exists() ? document.data() : null;
  }

  /**
   * Create a price grid
   * @param priceGrid The price grid object to create
   * @returns The price grid id
   */
  async addPriceGrid(priceGrid: CommercePriceGrid): Promise<string> {
    const ref = collection(this.firestore, 'price-grid')
      .withConverter(CommercePriceGrid.converter);
    const docRef = await addDoc(ref, priceGrid);
    return docRef.id;
  }

  /**
   * Update the price grid
   * @param priceGrid The price grid id to update
   * @returns Update doc promise
   */
  async updateGrid(priceGrid: CommercePriceGrid): Promise<void> {
    const ref = doc(this.firestore, 'price-grid', priceGrid.id)
      .withConverter(CommercePriceGrid.converter);
    return updateDoc(ref, {
      name: priceGrid.name,
      prices: priceGrid.prices,
      customers: priceGrid.customers,
      unit: priceGrid.unit,
      quantityMin: Number.isInteger(priceGrid.quantityMin) ? Number(priceGrid.quantityMin) : 0,
      quantityMax: Number.isInteger(priceGrid.quantityMax) ? Number(priceGrid.quantityMax) : 0,
      fixedCost: Number.isNaN(priceGrid.fixedCost) ? 0 : Number(priceGrid.fixedCost),
      minPrice: Number.isNaN(priceGrid.minPrice) ? 0 : Number(priceGrid.minPrice),
    });
  }

  /**
   * Delete the price grid
   * @param id Price grid id
   */
  async deletePriceGrid(id: string): Promise<void> {
    const ref = doc(this.firestore, 'price-grid', id)
      .withConverter(CommercePriceGrid.converter);
    await deleteDoc(ref);
  }

  /**
   * Count the number sales of a product
   * @param productId The product id
   * @returns Total sales of the product
   */
  async countSoldWebProducts(productId: string): Promise<number> {
    const q = query(
      collection(this.firestore, 'web-products'),
      where('product', '==', productId)
    ).withConverter(WebProduct.converter);
    const ref = await getDocs(q);
    ref.docs.filter(product => product.data().transactionId != '');
    return ref.docs.length;
  }

  /**
   * Fetches a list of packaging records associated with the specified organization ID.
   * @param organizationId - The ID of the organization whose packaging records are to be fetched.
   * @returns A promise that resolves to an array of packaging objects.
   */
  async listPackaging(organizationId: string): Promise<Packaging[]> {
    const q = query(
      collection(this.firestore, 'packagings'), where('organizationId', '==', organizationId))
      .withConverter(Packaging);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Generates a pricing grid template with predefined columns and step values.
   * @returns An object containing two properties:
   * - columns: An array of strings representing the template's column headers.
   * - steps: An array of numbers representing predefined step values.
   */
  getPricingGridTemplate(): { columns: string[], steps: number[] } {
    const steps = [1, 2 , 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 40, 50, 75, 100,
      150, 200, 250, 300, 400, 500, 750, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 7500, 10000, 15000];
    const surfaceLabel = this.translate.instant('SURFACE');
    const unitLabel = this.translate.instant('UNIT');
    const columns: string[] = [
      this.translate.instant('variationId'),
      `${this.translate.instant('name')}`,
      this.translate.instant('gridId'),
      `${this.translate.instant('variation.prices.name.label')}`,
      `${this.translate.instant('products.menu.title')}`,
      `${this.translate.instant('variation.unitType.unit')} (${unitLabel} / ${surfaceLabel}) `,
      `${this.translate.instant('variation.prices.quantityMin.label')}`,
      `${this.translate.instant('variation.prices.quantityMax.label')}`,
      `${this.translate.instant('variation.prices.fixedCost.label')}`,
      `${this.translate.instant('variation.prices.minPrice.label')}`,
    ]
    return { columns, steps }
  }

  /**
   * Downloads an Excel file containing pricing grids with specified details.
   * @param configurations - An array of configurations
   * Each grid contains pricing details such as range ("from", "to") and "price".
   */
  async download(configurations: CommerceVariation[]): Promise<void> {
    const workbook = utils.book_new();
    const { columns, steps } = this.getPricingGridTemplate();
    const sheet = utils.aoa_to_sheet([[...columns, ...steps]]);
    utils.book_append_sheet(workbook, sheet, this.translate.instant('database'));
    const rowOffSet = 1;
    for (const configuration of configurations) {
      const grids = await this.priceGridsSync(configuration.id);
      const products = await this.productService.productByConfiguration(configuration.id);
      for (const grid of grids) {
        const prices = [];
        for (const step of steps) {
          const price = grid.prices.find(p => p.from === step)?.price;
          if (!price) {
            prices.push(0);
            continue;
          }
          prices.push(price);
        }
        const columnsValues = [
          configuration.id,
          configuration.name,
          grid.id,
          grid.name,
          products.map(p => p.name).join(','),
          grid.unit === UnitTypeEnum.M2 ? this.translate.instant('SURFACE') : this.translate.instant('UNIT'),
          grid.quantityMin,
          grid.quantityMax,
          grid.fixedCost,
          grid.minPrice,
        ];
        const rowIndex = rowOffSet + configurations.indexOf(configuration);
        utils.sheet_add_aoa(sheet, [
          [...columnsValues, ...prices]
        ], { origin: { c: 0, r: rowIndex } } );
      }
    }
    const fileName = `database.xlsx`
    writeFileXLSX(workbook, fileName, { cellStyles: true });
  }

  /**
   * Uploads a file and processes its data to update the given price grids.
   * @param file The file to be uploaded and processed.
   * @returns A promise that resolves with the updated commerce price grids or rejects an error
   * if processing fails.
   */
  public upload(file: File): Promise<GridUpdate[]> {
    return new Promise((resolve, reject) => {
      const reader: FileReader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onerror = (e: ProgressEvent<FileReader>): void => reject(e);
      reader.onload = async (e: ProgressEvent<FileReader>): Promise<void> => {
        const workbook: WorkBook = read(e.target.result);
        const csv = utils.sheet_to_csv(workbook.Sheets[this.translate.instant('database')], { rawNumbers: true })
        if (!csv) {
          return resolve([]);
        }
        const result = await this.parseCSVAsync(csv);
        const [, ...dataRows] = result;
        const gridUpdates: GridUpdate[] = [];
        for (const dataRow of dataRows) {
          const [variationId, name, gridId, gridName, products, unit, quantityMin,
            quantityMax, fixedCost, minPrice, ...steps] = dataRow;
          if (!variationId) {
            continue;
          }
          const gridUpdate = new GridUpdate({
            variationId,
            name,
            gridId,
            gridName,
            products,
            unit,
            quantityMin,
            quantityMax,
            fixedCost,
            minPrice,
            prices: [...steps].map(value=> Number(value)),
          });
          gridUpdates.push(gridUpdate);
        }
        resolve(gridUpdates);
      };
    });
  }

  /**
   * Asynchronously parses a CSV string into a JavaScript object or array format.
   * @param csv - The CSV string to be parsed.
   * @returns A promise that resolves with the parsed CSV data or rejects with an error if parsing fails.
   */
  parseCSVAsync(csv: string): Promise<any> {
    return new Promise((resolve, reject) => {
      Papa.parse(csv, {
        worker: true,
        complete: results => {
          resolve(results.data); // Resolves with the parsed data
        },
        error: error => {
          reject(error); // Rejects if there's an error
        },
      });
    });
  }

  /**
   * Processes a grid of prices by sorting them based on the "from" value
   * and mapping each price range to include an upper limit ("to").
   * @param grid - The grid of prices to process. It includes an array of price objects, each
   * containing "from" and "price" properties.
   * @returns An array of processed price ranges, each containing the "from" value, a calculated "to" value,
   * and the "price".
   */
  prices(grid: CommercePriceGrid): { from: number, to: number, price: number }[] {
    grid?.prices.sort((a, b) => a.from - b.from);
    return grid?.prices.map((price, idx, arr) => ({
      ...price,
      to: arr[idx + 1]?.from ?? Number.POSITIVE_INFINITY
    })) ?? [];
  }

  /**
   * Unsubscribe from all service subscriptions
   */
  unsubscribe(): void {
    if (this.unsubscribeCategories) {
      this.unsubscribeCategories();
      this.categories$ = null;
    }
    if (this.unsubscribeOptions) {
      this.unsubscribeOptions.forEach(unsubscribe => unsubscribe());
      this.options$ = null;
    }
  }
}
